<template>
    <div class="step-content">
        <slot name="title" />

        <div class="main-content">
            <div v-show="active" class="input-fields">
                <div class="recipients-field">
                    <RecipientAddressField
                        v-model="toAddress"
                        :submitted="submitted"
                        data-qa="recipient-address-field"
                        @custom-value-selected="toAddress = $event"
                    />
                </div>

                <div class="action-buttons">
                    <DsOutlineButton
                        class="save"
                        data-qa="save-button"
                        @click="save"
                    >
                        {{ $t('global.save') }}
                    </DsOutlineButton>

                    <DsTextButton
                        class="cancel"
                        @click.stop="cancel"
                    >
                        {{ $t('global.cancel') }}
                    </DsTextButton>
                </div>
            </div>

            <EmailBroadcastStepOverview v-show="!active" :sections="overviewSections" />
        </div>

        <slot name="action" :actionText="$t('email.broadcast.steps.audience.actionText')" />
    </div>
</template>

<script>
import EmailBroadcastStepOverview from './EmailBroadcastStepOverview';
import RecipientAddressField from '@/components/unlayer/RecipientAddressField';
import { STEPS } from '@/components/unlayer/constants/editor.constants';
import { isSpaApp } from '@/api/shared';
import { DEFAULT_RECIPIENT_VALUES } from '@/components/broadcast/constants/emailBroadcast.constants';
import {
    REGEX_CUSTOM_EMAIL_ADDRESS_WITH_MERGE_FIELD_SUPPORT,
} from '@/utils/email.util';

export default {
    components: {
        EmailBroadcastStepOverview,
        RecipientAddressField,
    },

    props: {
        active: Boolean,
        email: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            submitted: false,
            hasError: false,
            toAddress: DEFAULT_RECIPIENT_VALUES.EMAIL,
            isSpaApp: true,
        };
    },

    async created() {
        this.isSpaApp = await isSpaApp();
    },

    watch: {
        email: {
            handler() {
                this.syncData();
            },
            deep: true,
            immediate: true,
        },
    },

    computed: {
        overviewSections() {
            return [{
                content: this.overViewLabel,
                placeholder: this.$t('unlayerBuilder.emailRecipient.label.placeholder'),
                title: this.$t('global.to'),
            }];
        },

        overViewLabel() {
            const isSpa = this.isSpaApp;
            const defaultLabel = isSpa ? this.$t('unlayerBuilder.emailRecipient.label.work') : this.$t('unlayerBuilder.emailRecipient.label.email');

            if (!this.toAddress) {
                return defaultLabel;
            }

            if (Object.values(DEFAULT_RECIPIENT_VALUES).includes(this.toAddress)) {
                switch (this.toAddress) {
                case DEFAULT_RECIPIENT_VALUES.EMAIL:
                    return defaultLabel;
                case DEFAULT_RECIPIENT_VALUES.EMAIL2:
                    return isSpa ? this.$t('unlayerBuilder.emailRecipient.label.personal') : this.$t('unlayerBuilder.emailRecipient.label.emailAddress2');
                case DEFAULT_RECIPIENT_VALUES.EMAIL3:
                    return isSpa ? this.$t('unlayerBuilder.emailRecipient.label.other') : this.$t('unlayerBuilder.emailRecipient.label.emailAddress3');
                default:
                    return defaultLabel;
                }
            }

            if (REGEX_CUSTOM_EMAIL_ADDRESS_WITH_MERGE_FIELD_SUPPORT.test(this.toAddress)) {
                const [, name,, emailAddress] = REGEX_CUSTOM_EMAIL_ADDRESS_WITH_MERGE_FIELD_SUPPORT.exec(this.toAddress);

                return name ? `${name} <${emailAddress}>` : `${emailAddress}`;
            }

            return defaultLabel;
        },
    },

    methods: {
        cancel() {
            this.hasError = false;
            this.syncData();
            this.$emit('cancel');
        },

        save() {
            this.validate();

            if (!this.hasError) {
                this.$emit('save', {
                    toAddress: this.toAddress,
                });
            }
        },

        validate() {
            if (!Object.values(DEFAULT_RECIPIENT_VALUES).includes(this.toAddress)) {
                this.submitted = true;
            }

            this.hasError = !REGEX_CUSTOM_EMAIL_ADDRESS_WITH_MERGE_FIELD_SUPPORT.test(this.toAddress);
        },

        syncData() {
            const { [STEPS.AUDIENCE]: { toAddress } } = this.email;

            const isValid = REGEX_CUSTOM_EMAIL_ADDRESS_WITH_MERGE_FIELD_SUPPORT.test(toAddress);

            if (!isValid) {
                this.toAddress = DEFAULT_RECIPIENT_VALUES.EMAIL;

                this.$emit('save', {
                    toAddress: this.toAddress,
                });
            } else {
                this.toAddress = toAddress;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import './steps';

.input-fields {
    --mergable-input-margin-bottom: #{$spacing-200};
}

.action-buttons {
    display: flex;
    align-items: flex-start;
}
</style>
